import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActingAthlete } from '../context/ActingAthleteContext';

// Reminder links carry `?for=<athlete user id>` so a guardian linked to several
// athletes lands on the athlete the reminder is about. When the signed-in user
// is a guardian linked to that athlete, the acting athlete is switched BEFORE
// the page renders; for anyone else (an athlete, a guardian not linked to that
// id) the parameter is ignored. A `for` seen while signed out is kept for this
// browser tab and applied right after sign-in.
const PENDING_KEY = 'acting_athlete_pending_for';

const readPending = () => {
  try { return sessionStorage.getItem(PENDING_KEY); } catch { return null; }
};
const writePending = (value) => {
  try {
    if (value) sessionStorage.setItem(PENDING_KEY, value);
    else sessionStorage.removeItem(PENDING_KEY);
  } catch { /* storage unavailable: the link still works while signed in */ }
};

const parseId = (raw) => {
  const n = Number.parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 && String(n) === String(raw).trim() ? n : null;
};

export default function ActingAthleteFromLink({ children }) {
  const { search } = useLocation();
  const { isAuthenticated, loading, user, API_URL } = useAuth();
  const { actingAthleteId, enterAthlete } = useActingAthlete();
  const fromUrl = parseId(new URLSearchParams(search).get('for'));
  const target = fromUrl || parseId(readPending());
  const [settled, setSettled] = useState(null); // the target id this gate finished with

  const isGuardian = user && user.role === 'guardian';
  // Hold the page until one render AFTER the acting athlete matches: the
  // provider installs its as_athlete_id interceptor in an effect, and a
  // page's own fetch effects would otherwise fire first.
  const needsSwitch = Boolean(target && isAuthenticated && isGuardian && settled !== target);

  useEffect(() => {
    if (!target || loading) return undefined;
    if (!isAuthenticated) {
      if (fromUrl) writePending(String(fromUrl));
      return undefined;
    }
    if (!isGuardian || actingAthleteId === target) {
      writePending(null);
      if (isGuardian) setSettled(target);
      return undefined;
    }
    if (settled === target) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/guardians/my-athletes`)
      .then(({ data }) => {
        if (cancelled) return;
        const match = (data || []).find((a) => a.athlete_user_id === target);
        if (match) {
          enterAthlete(match);          // settled on the next run, once it is in effect
        } else {
          writePending(null);
          setSettled(target);           // not linked: ignore the link's athlete
        }
      })
      .catch(() => {
        if (cancelled) return;
        writePending(null);
        setSettled(target);
      });
    return () => { cancelled = true; };
  }, [target, fromUrl, loading, isAuthenticated, isGuardian, actingAthleteId, settled, API_URL, enterAthlete]);

  if (needsSwitch) {
    return <div className="loading-spinner">Loading...</div>;
  }
  return children;
}
